/**
 * Clinic-timezone-aware date/time helpers.
 *
 * The backend now stamps and stores every timestamp as *wall-clock time in the clinic's configured
 * timezone* (a naive `LocalDateTime`/`LocalDate`, no offset — e.g. "2026-06-04T14:30:00"). These
 * helpers render those values as the clinic's wall clock regardless of the viewer's browser
 * timezone, and expose the clinic's "today"/"now" for client-side date logic (filters, default
 * pickers) so a receptionist on a laptop set to another timezone still sees and files things in
 * clinic time.
 *
 * Two cases, handled transparently:
 *  - Naive backend strings (the normal case): rendered by their literal components, with NO
 *    timezone math — so the digits the backend sent are exactly the digits shown, in any browser.
 *  - Values that carry an explicit zone (trailing `Z` or `±HH:MM`, e.g. a real instant): treated as
 *    a true instant and converted into the clinic zone via `Intl`.
 *
 * The active zone is set once at app startup from `GET /api/locations/timezone` (see App.js) and
 * defaults to Asia/Karachi — correct for this Pakistani clinic even before the fetch resolves.
 */

let activeZone = 'Asia/Karachi';

/** Common short labels for zones where the Intl `short` name is an unfriendly "GMT+5". */
const ZONE_ABBREV = {
  'Asia/Karachi': 'PKT',
};

/** Set the clinic-wide timezone (IANA id). Called from App.js after fetching the active location. */
export const setClinicTimezone = (tz) => {
  if (tz && typeof tz === 'string') activeZone = tz.trim();
};

export const getClinicTimezone = () => activeZone;

/** A short label for the active zone, e.g. "PKT" or "GMT+5". */
export const getClinicTimezoneLabel = () => {
  if (ZONE_ABBREV[activeZone]) return ZONE_ABBREV[activeZone];
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: activeZone,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    const tzPart = parts.find((p) => p.type === 'timeZoneName');
    return tzPart ? tzPart.value : activeZone;
  } catch {
    return activeZone;
  }
};

const ZONED_SUFFIX = /([zZ]|[+-]\d{2}:?\d{2})$/;

/** True if the string carries an explicit zone (real instant) rather than naive wall-clock. */
const isZoned = (value) => typeof value === 'string' && ZONED_SUFFIX.test(value.trim());

/**
 * Turn any accepted input into a Date that, when formatted WITHOUT a `timeZone` option, renders the
 * intended clinic wall-clock components. For naive strings we build a browser-local Date from the
 * parsed components (so the literal digits survive). For zoned strings / Date objects / epoch ms we
 * convert the true instant into the clinic zone first. Returns null on unparseable input.
 *
 * Exported because this is also the only correct way to COMPARE a backend date against a local
 * boundary. `new Date('2026-08-14')` is parsed as UTC midnight by spec, which lands on the previous
 * day for any browser west of UTC — a date-range filter built that way silently drops a whole day.
 * Anything comparing a LocalDate field must come through here.
 */
export const toClinicDate = (value) => {
  if (value === null || value === undefined || value === '') return null;

  // Naive "YYYY-MM-DD[ T]HH:mm[:ss]" — render components literally, no tz shift.
  if (typeof value === 'string' && !isZoned(value)) {
    const m = value
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (!m) {
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    }
    return new Date(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      m[4] ? Number(m[4]) : 0,
      m[5] ? Number(m[5]) : 0,
      m[6] ? Number(m[6]) : 0,
    );
  }

  // Zoned string, Date, or epoch — a true instant. Re-express it in the clinic zone by reading the
  // clinic-zone components back out and rebuilding a browser-local Date from them.
  const instant = value instanceof Date ? value : new Date(value);
  if (isNaN(instant.getTime())) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: activeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(instant);
    const get = (t) => Number(parts.find((p) => p.type === t)?.value);
    let hour = get('hour');
    if (hour === 24) hour = 0; // some engines emit "24" for midnight
    return new Date(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'));
  } catch {
    return instant;
  }
};

const DATE_DEFAULTS = { day: 'numeric', month: 'long', year: 'numeric' };
const TIME_DEFAULTS = { hour: 'numeric', minute: '2-digit' };

/** Format a date only (no time). Pass Intl options to override the default "6 June 2026" style. */
export const formatClinicDate = (value, opts = {}, fallback = '—') => {
  const d = toClinicDate(value);
  if (!d) return fallback;
  return d.toLocaleDateString('en-GB', { ...DATE_DEFAULTS, ...opts });
};

/** Format time only, e.g. "2:30 PM". */
export const formatClinicTime = (value, opts = {}, fallback = '—') => {
  const d = toClinicDate(value);
  if (!d) return fallback;
  return d.toLocaleTimeString('en-US', { ...TIME_DEFAULTS, ...opts });
};

/**
 * Format date + time, e.g. "6 Jun 2026, 2:30 PM". Pass `{ withZone: true }` to append the clinic
 * zone label ("… PKT"). Date/time Intl options can be overridden via `dateOpts` / `timeOpts`.
 */
export const formatClinicDateTime = (value, { withZone = false, dateOpts, timeOpts } = {}, fallback = '—') => {
  const d = toClinicDate(value);
  if (!d) return fallback;
  const datePart = d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...dateOpts,
  });
  const timePart = d.toLocaleTimeString('en-US', { ...TIME_DEFAULTS, ...timeOpts });
  const out = `${datePart}, ${timePart}`;
  return withZone ? `${out} ${getClinicTimezoneLabel()}` : out;
};

/** The clinic's current date as "YYYY-MM-DD" — for date filters and DatePicker `max`/defaults. */
export const clinicToday = () => {
  try {
    // en-CA gives ISO "YYYY-MM-DD".
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: activeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().split('T')[0];
  }
};

/** The clinic's current wall-clock as a browser-local Date (for relative comparisons / display). */
export const clinicNow = () => toClinicDate(new Date());
